<template>
  <v-card>
    <v-skeleton-loader v-if="loading" type="text"></v-skeleton-loader>
    <v-card-title>Crawl Requests</v-card-title>
    <v-card-subtitle>
      <DateRangePicker
        :ranges="ranges"
        v-model="dateRange"
        opens="right"
        :singleDatePicker="false"
        :maxDate="new Date()"
        @update="getChartData"
      >
        <template v-slot:input="picker">
          {{ picker.startDate | date }} - {{ picker.endDate | date }}
        </template>
      </DateRangePicker>
    </v-card-subtitle>
    <div class="stats__chart">
      <bar-chart :chartData="formattedChartData" />
    </div>
  </v-card>
</template>

<script>
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";
import moment from "moment";
import { API } from "../../api";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import BarChart from "../Charts/BarChart.vue";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default {
  name: "ForestCrawlRequests",
  components: {
    BarChart,
    DateRangePicker,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  filters: {
    date(date) {
      if (!date) return "";
      return date.toISOString().substr(0, 10).split("-").reverse().join(".");
    },
  },
  data() {
    return {
      dateRange: {
        startDate: moment().subtract(7, "d").format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
      },
      loaded: false,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
      },
      dataSet: [],
      totalCrawlRequests: 0,
    };
  },
  created() {
    this.getChartData();
  },
  computed: {
    formattedChartData() {
      return {
        labels: this.dataSet.map((item) => item.x),
        datasets: [
          {
            label: `Crawl Requests (${this.totalCrawlRequests})`,
            backgroundColor: "#5CB660",
            borderColor: "#5CB660",
            data: this.dataSet.map((item) => item.y),
          },
        ],
      };
    },
    ranges() {
      const startOfDay = new Date(new Date().setHours(2, 0, 0, 0));
      const endOfDay = new Date(new Date().setHours(23, 59, 59, 999));

      const thisMonthStart = new Date(
        startOfDay.getFullYear(),
        startOfDay.getMonth(),
        1
      );
      const thisMonthEnd = new Date(
        endOfDay.getFullYear(),
        endOfDay.getMonth() + 1,
        0
      );

      return {
        Today: [startOfDay, endOfDay],
        "Last 7 days": [
          new Date(
            startOfDay.getFullYear(),
            startOfDay.getMonth(),
            startOfDay.getDate() - 7
          ),
          endOfDay,
        ],
        "Last 30 days": [
          new Date(
            startOfDay.getFullYear(),
            startOfDay.getMonth(),
            startOfDay.getDate() - 30
          ),
          endOfDay,
        ],
        "This month": [thisMonthStart, thisMonthEnd],
        "This Year": [
          new Date(startOfDay.getFullYear(), 0, 1),
          new Date(endOfDay.getFullYear(), 11, 31),
        ],
        "Last month": [
          new Date(startOfDay.getFullYear(), startOfDay.getMonth() - 1, 1),
          new Date(endOfDay.getFullYear(), endOfDay.getMonth(), 0),
        ],
        "Last Year": [
          new Date(startOfDay.getFullYear() - 1, 0, 1),
          new Date(endOfDay.getFullYear() - 1, 11, 31),
        ],
      };
    },
  },
  methods: {
    formatChartData(chartData) {
      this.dataSet = [];

      if (
        moment(this.dateRange.startDate).format("YYYY-MM-DD") ===
        moment(this.dateRange.endDate).format("YYYY-MM-DD")
      ) {
        for (let index = 0; index < 24; index++) {
          const currentTotal =
            chartData.filter((el) => moment(el?.createdAt).hour() === index)
              ?.length ?? 0;

          this.dataSet.push({
            x: index,
            y: currentTotal,
          });
        }
      } else {
        const start = new Date(this.dateRange.startDate);
        start.setDate(start.getDate() - 1);
        const end = new Date(this.dateRange.endDate);

        for (
          const iterableDate = end;
          iterableDate.toISOString().substr(0, 10) !==
          start.toISOString().substr(0, 10);
          iterableDate.setDate(iterableDate.getDate() - 1)
        ) {
          const dateFormatted = iterableDate.toISOString().substr(0, 10);

          const currentTotal =
            chartData.filter(
              (el) =>
                moment(el?.createdAt).format("YYYY-MM-DD") === dateFormatted
            )?.length ?? 0;

          this.dataSet.push({
            x: dateFormatted,
            y: currentTotal,
          });
        }

        this.dataSet.reverse();
      }
    },
    async getChartData() {
      this.loading = true;
      try {
        const { data, count } = await API.getVisitors({
          forestId: this.id,
          type: "crawlReview",
          pageSize: 0,
          ...this.dateRange,
        });

        this.totalCrawlRequests = count;

        this.formatChartData(data);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
